import { z } from "zod";
import {
  Box,
  Text,
  WidgetEmpty,
  WidgetListRow,
  WidgetSkeleton,
  useWidgetInstance,
  useWidgetSurface,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
  WidgetListHeader,
  type WidgetListColumn,
  useElementSize,
  WidgetBleedScroller,
  widgetListColumnsFor,
} from "@salestrak/ui";

import { EntityGroupTag, EntityTag } from "#app/components/entity";
import { NONE_OPTION_LABEL } from "#app/lib/custom-field-utils";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { useWorkflowRoster, type WorkflowRosterRow } from "./use-workflow-roster";

/**
 * `workflow-roster` — one line per workflow in reach: the workflow, its
 * project count, and the faces of who is working in it. On a team board the
 * roster is the team's reach (every workflow the team is granted on, a
 * workflow with nothing in it included) and the faces are the team's own
 * members; elsewhere it is every live workflow the viewer can see and every
 * assignee. A row with projects opens the Data view on them; an empty row is
 * a fact, not a door.
 */
export const workflowRosterConfigSchema = z.looseObject({
  showMembers: z.boolean().default(true),
  filters: widgetFiltersSchema.nullish(),
});

export type WorkflowRosterConfig = z.infer<typeof workflowRosterConfigSchema>;

/** Faces before the stack folds to "+N". */
const MEMBER_STACK_MAX = 5;

/**
 * The row's trailing columns, named once in the list header so a row is one
 * line: the member stack (folds away under 420px of body, and with
 * `showMembers` off) and the project count, always. Measured off the body,
 * never the grid span.
 */
const WORKFLOW_COLUMNS = [
  { key: "members", label: "Members", width: 72, minWidth: 420 },
  { key: "projects", label: "Projects", width: 64, minWidth: 0 },
] as const satisfies readonly WidgetListColumn[];
type WorkflowColumn = (typeof WORKFLOW_COLUMNS)[number];

function workflowColumnsFor(bodyWidth: number, showMembers: boolean): readonly WorkflowColumn[] {
  return widgetListColumnsFor(WORKFLOW_COLUMNS, bodyWidth).filter(
    (column) => column.key !== "members" || showMembers,
  );
}

function WorkflowRow({
  row,
  columns,
  onOpen,
}: {
  row: WorkflowRosterRow;
  columns: readonly WorkflowColumn[];
  onOpen: (() => void) | undefined;
}) {
  const surface = useWidgetSurface();
  const empty = row.total === 0;
  const faces = row.members.flatMap((member) => (member.entity ? [member.entity] : []));
  return (
    <WidgetListRow onClick={onOpen}>
      <Box fill="flex" shrink minWidth={0}>
        <EntityTag
          entity={{ kind: "workflow", id: row.workflow.workflowId, workflow: row.workflow }}
          display="full"
          interactive={false}
          weight="medium"
        />
      </Box>
      {columns.map((column) => (
        <Box key={column.key} width={column.width} shrink={false} align="end">
          {column.key === "members" ? (
            faces.length > 0 ? (
              <EntityGroupTag
                entities={faces}
                allIds={row.members.map((member) => member.userId)}
                total={row.members.length}
                max={MEMBER_STACK_MAX}
                size="xs"
                variant="stack"
                surface={surface}
                interactive={false}
                hoverDisabled
                tooltip
              />
            ) : (
              <Text size="sm" variant="faint">
                {NONE_OPTION_LABEL}
              </Text>
            )
          ) : (
            <Text size="sm" weight="semibold" variant={empty ? "faint" : undefined} tabular>
              {empty ? NONE_OPTION_LABEL : row.total.toLocaleString()}
            </Text>
          )}
        </Box>
      ))}
    </WidgetListRow>
  );
}

function WorkflowRosterRender({ config }: WidgetRenderContext<WorkflowRosterConfig>) {
  const { rows, loading, error } = useWorkflowRoster(config.filters);
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  const { ref: bodyRef, size: bodySize } = useElementSize<HTMLDivElement>();
  const columns = workflowColumnsFor(bodySize.width, config.showMembers);

  if (error) throw error;
  // The count hooks hold the previous answer through a re-query, so the rows
  // stand; only the first paint has nothing to show yet.
  if (loading && rows.length === 0) {
    // The header's labels are static copy, so it stands real over anonymous
    // rows on the roster's rung, figure bars under the columns the width keeps.
    return (
      <Box ref={bodyRef} direction="column" grow minHeight={0}>
        <WidgetListHeader identity="Workflow" columns={columns} />
        <WidgetSkeleton variant="list" figures={columns.map((column) => column.width)} />
      </Box>
    );
  }
  if (rows.length === 0) {
    return <WidgetEmpty title="No workflows" />;
  }

  return (
    // The header names the columns once and stays put; the rows scroll under
    // it. The scroller bleeds `sm` and the content pads back in, so the rows'
    // hover wash (a negative margin) stays inside the viewport and never
    // scrolls sideways. Columns follow the MEASURED body width.
    <Box ref={bodyRef} direction="column" grow minHeight={0}>
      <WidgetListHeader identity="Workflow" columns={columns} />
      <WidgetBleedScroller inset="sm">
        {rows.map((row) => (
          <WorkflowRow
            key={row.workflow.workflowId}
            row={row}
            columns={columns}
            onOpen={
              drill && instance && row.total > 0
                ? () =>
                    drill.openData(instance.instanceId, {
                      kind: "dimension",
                      dimension: "workflow",
                      ids: [row.workflow.workflowId],
                      label: row.workflow.name ?? "",
                    })
                : undefined
            }
          />
        ))}
      </WidgetBleedScroller>
    </Box>
  );
}

const workflowRosterSettings: WidgetSettingsGroup[] = [
  {
    title: "Display",
    fields: [
      {
        key: "showMembers",
        kind: "toggle",
        label: "Show who is working in each workflow",
        icon: "users",
      },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const workflowRosterDef: WidgetModule<WorkflowRosterConfig> = {
  type: "workflow-roster",
  title: "Workflows",
  icon: "workflow",
  span: { minW: 6, minH: 3, maxW: 12, maxH: 12, defaultW: 12 },
  // The roster owns its scrollport under the sticky column header, so it
  // drops the card's end inset alone (`"bottom"`, never `true` — the rows'
  // hover wash needs the side inset to bleed into) and carries the scroll-end
  // gutter as its own content, reaching the card's edge.
  body: "bleed-bottom",
  configSchema: workflowRosterConfigSchema,
  settings: workflowRosterSettings,
  headerActions: WidgetHeaderFilters,
  // A row is "who is on what", so its drill opens grouped by assignee: one
  // group per face in the row's stack.
  drilldownGroupBy: "assignees",
  catalog: [
    {
      key: "workflow-roster.default",
      label: "Workflows",
      description:
        "Every workflow in reach with its Project count and who is working in it — empty workflows included.",
      section: "workflows" satisfies WidgetSection,
      tags: ["projects", "workflows", "members"] satisfies readonly WidgetTag[],
      defaultW: 12,
    },
  ],
  render: WorkflowRosterRender,
};
